// Global Instructions Rule Applied!
//
// Investor Statements section of the Statements tab (spec A3.14 stage 3):
// the documents investors receive for a quarter, previewed from the live
// books - the Fund Financial Statements (the same for every investor) and
// the Capital Account Statement of one investor. For a consolidated fund the
// capital account statement is the consolidated one or the investor's
// account in one legal entity.
//
// Nothing is stored here: a preview is a live rendering of the document as it
// would be generated now (the capital call notice preview is the pattern -
// NoticesSection.tsx), and Download PDF prints that same document.
//
// The checks decide whether a statement can be made at all. A blocking check
// (the ledger's investments do not reconcile to the investment records, the
// balance sheet is out of balance, a held position has no valuation, ...)
// disables the previews and says which check failed and why; the backend
// refuses the render too. Draft transactions only warn.
//
// Period ends are quarter ends: the statements are quarterly, and every
// fund's financial year is a calendar year.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal, Select, Table, Tag, Tooltip, Typography } from 'antd'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { fetchFundStatementChecks } from '../../../services/fundsService.ts'
import { fetchFundGroupStatementChecks } from '../../../../fund_groups/services/fundGroupsService.ts'
import { ReportPreviewPane } from '../../../../document_reports/components/ReportPreviewPane.tsx'
import {
  fetchReportPdf,
  fetchReportPreview,
  saveBlob,
  type DocumentReportRequest
} from '../../../../document_reports/services/documentReportsService.ts'
import type {
  InvestorStatementChecks,
  StatementCheck
} from '../../../../../state_manager/data_models/investor_statement_checks.ts'
import type { FundStatementLpOption } from '../../../../../state_manager/data_models/fund_statements.ts'

const { Text } = Typography

const REPORT_FUND_PACK = 'fund_financial_statements'
const REPORT_CAPITAL_ACCOUNT = 'capital_account_statement'
// the consolidated statement, in the picker of a consolidated fund
const CONSOLIDATED = ''

const money = (n: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** The last day of the quarter a date falls in. */
const quarterEnd = (d: Dayjs): Dayjs =>
  d
    .month(Math.floor(d.month() / 3) * 3 + 2)
    .endOf('month')
    .startOf('day')

/** The last quarter that has ended: statements are made for a finished quarter. */
export const lastCompletedQuarterEnd = (today: Dayjs = dayjs()): Dayjs =>
  quarterEnd(today.startOf('month').subtract((today.month() % 3) + 1, 'month'))

/** 'not_reconciled' -> 'Not reconciled' */
const checkLabel = (code: string): string => {
  const words = code.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

interface PreviewState {
  title: string
  request: DocumentReportRequest
  blobUrl: string | null
  pageCount: number | null
  loading: boolean
  error: string | null
}

interface InvestorStatementsSectionProps {
  fundId: string | null
  /** Consolidated mode: the statements of the consolidated fund. */
  group?: { fundGroupId: string; primaryFundId: string | null }
  /** The tab's LP list: commitments of a fund, consolidated commitments of a
   *  consolidated fund. */
  lpOptions: FundStatementLpOption[]
}

export default function InvestorStatementsSection({ fundId, group, lpOptions }: InvestorStatementsSectionProps) {
  const fundGroupId = group?.fundGroupId ?? null
  const [periodEnd, setPeriodEnd] = useState<Dayjs>(() => lastCompletedQuarterEnd())
  const [checks, setChecks] = useState<InvestorStatementChecks | null>(null)
  const [checksError, setChecksError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedLp, setSelectedLp] = useState<string | null>(null)
  const [member, setMember] = useState<string>(CONSOLIDATED)
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [downloading, setDownloading] = useState(false)

  const periodEndIso = periodEnd.format('YYYY-MM-DD')

  const loadChecks = useCallback(async () => {
    if (!fundId && !fundGroupId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = fundGroupId
        ? await fetchFundGroupStatementChecks(fundGroupId, periodEndIso, token)
        : await fetchFundStatementChecks(fundId as string, periodEndIso, token)
      setChecks(res.data)
      setChecksError(res.error ?? null)
    } finally {
      setLoading(false)
    }
  }, [fundId, fundGroupId, periodEndIso])

  useEffect(() => {
    void loadChecks()
  }, [loadChecks])

  // One option per statement: an investor's commitments in a fund make ONE
  // statement, named by the holder's lead commitment
  // (prog__statement_fund_commitment_id), so the commitments that share it
  // are one entry under the plain holder name. A prior holder of a
  // transferred commitment has no statement of its own: the statement is the
  // holder's at the period end. A read that predates the subject id lists
  // each commitment as its own subject.
  const investorOptions = useMemo(() => {
    const subjects = new Map<string, string>()
    lpOptions.forEach((lp) => {
      if (lp.fund_commitment_transfers__id) return
      const subject = lp.prog__statement_fund_commitment_id ?? lp.fund_commitments__id
      if (!subjects.has(subject)) subjects.set(subject, lp.prog__statement_label ?? lp.investors__name)
    })
    return Array.from(subjects, ([value, label]) => ({ value, label }))
  }, [lpOptions])

  // the legal entities of a consolidated fund, as the checks name them
  const memberOptions = useMemo(
    () => [
      { value: CONSOLIDATED, label: 'Consolidated' },
      ...(checks?.reconciliation ?? []).map((r) => ({ value: r.funds__id, label: r.funds__name ?? r.funds__id }))
    ],
    [checks]
  )

  const closePreview = useCallback(() => {
    setPreview((current) => {
      if (current?.blobUrl) URL.revokeObjectURL(current.blobUrl)
      return null
    })
  }, [])

  /** A live rendering of the document as it would be generated now. */
  const openPreview = useCallback(async (title: string, request: DocumentReportRequest) => {
    setPreview({ title, request, blobUrl: null, pageCount: null, loading: true, error: null })
    const result = await fetchReportPreview(request, getAccessToken(store.getState().auth))
    setPreview((current) => {
      // closed, or another preview opened, while this one rendered
      if (!current || current.request !== request || !current.loading) {
        if (result.blobUrl) URL.revokeObjectURL(result.blobUrl)
        return current
      }
      return {
        ...current,
        blobUrl: result.blobUrl ?? null,
        pageCount: result.pageCount ?? null,
        loading: false,
        error: result.success ? null : result.error || 'Failed to render the statement'
      }
    })
  }, [])

  const entityParams = useMemo(
    () => ({ ...(fundGroupId ? { fund_group_id: fundGroupId } : { fund_id: fundId }), period_end: periodEndIso }),
    [fundId, fundGroupId, periodEndIso]
  )

  const previewPack = useCallback(() => {
    void openPreview('Fund Financial Statements', { reportCode: REPORT_FUND_PACK, params: entityParams })
  }, [openPreview, entityParams])

  const previewCapitalAccount = useCallback(() => {
    if (!selectedLp) return
    const investor = investorOptions.find((o) => o.value === selectedLp)?.label ?? ''
    const entity = member ? memberOptions.find((o) => o.value === member)?.label : null
    void openPreview(`Capital Account Statement — ${investor}${entity ? ` — ${entity}` : ''}`, {
      reportCode: REPORT_CAPITAL_ACCOUNT,
      params: { ...entityParams, fund_commitment_id: selectedLp, ...(member ? { member } : {}) }
    })
  }, [openPreview, entityParams, selectedLp, member, investorOptions, memberOptions])

  /** The previewed document, printed to PDF. */
  const downloadPreview = useCallback(async () => {
    if (!preview) return
    setDownloading(true)
    try {
      const result = await fetchReportPdf(preview.request, getAccessToken(store.getState().auth))
      if (result.success && result.blob) saveBlob(result.blob, result.filename ?? 'statement.pdf')
      else showErrorToast(result.error || 'Failed to download the statement')
    } finally {
      setDownloading(false)
    }
  }, [preview])

  const blocked = checks ? !checks.passed : true
  const blockedTip = checks && !checks.passed ? 'A check below has to pass before a statement can be made.' : ''

  const blockingColumns = [
    {
      title: 'Fund',
      dataIndex: 'funds__name',
      width: 180,
      render: (v: string | null | undefined) => v ?? (fundGroupId ? 'Consolidated' : '-')
    },
    { title: 'Check', dataIndex: 'code', width: 240, render: checkLabel },
    { title: 'Why', dataIndex: 'message' }
  ]

  return (
    <FormSection
      title='Investor Statements'
      titleColor='blue'
      loading={loading}
      className='mt-3'
      headerActions={
        <div className='flex items-center gap-2'>
          <DatePicker
            size='small'
            picker='quarter'
            allowClear={false}
            aria-label='Period end'
            value={periodEnd}
            // "2026-Q2 (06/30/2026)": the quarter and the day the statements are made up to
            format={(d) => `${d.year()}-Q${Math.floor(d.month() / 3) + 1} (${quarterEnd(d).format('MM/DD/YYYY')})`}
            // a statement is made for a quarter that has ended
            disabledDate={(d) => quarterEnd(d).isAfter(dayjs())}
            onChange={(d) => {
              if (d) setPeriodEnd(quarterEnd(d))
            }}
          />
          <Button size='small' icon={<ReloadOutlined />} onClick={() => void loadChecks()}>
            Refresh
          </Button>
        </div>
      }
    >
      {checksError ? <Text type='danger'>{checksError}</Text> : null}

      {checks ? (
        <div className='mb-2 flex flex-wrap items-center gap-2'>
          <Text type='secondary'>Period ended {periodEnd.format('MM/DD/YYYY')}</Text>
          {checks.passed ? <Tag color='green'>Ready</Tag> : <Tag color='red'>Blocked</Tag>}
          {checks.reconciliation.map((r) => (
            <Tooltip
              key={r.funds__id}
              title={
                `Ledger, investments at cost: ${money(r.prog__ledger_cost)}. ` +
                `Investment records, cost held: ${money(r.prog__cost_held)}. ` +
                `Difference: ${money(r.prog__difference)}.`
              }
            >
              <Tag color={r.prog__reconciled ? 'green' : 'gold'}>
                {fundGroupId && r.funds__name ? `${r.funds__name}: ` : ''}
                {r.prog__reconciled ? 'Reconciled' : 'Not reconciled'}
              </Tag>
            </Tooltip>
          ))}
          {checks.warnings.map((w: StatementCheck, i) => (
            <Tooltip key={`${w.code}-${w.funds__id ?? i}`} title={w.message}>
              <Tag color='gold'>
                {fundGroupId && w.funds__name ? `${w.funds__name}: ` : ''}
                {checkLabel(w.code)}
              </Tag>
            </Tooltip>
          ))}
        </div>
      ) : null}

      {checks && checks.blocking.length > 0 ? (
        <Table
          size='small'
          className='mb-3'
          columns={blockingColumns}
          dataSource={checks.blocking.map((b, i) => ({ ...b, key: `${b.code}-${b.funds__id ?? ''}-${i}` }))}
          pagination={false}
          rowKey='key'
          style={{ maxWidth: 980 }}
        />
      ) : null}

      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-2'>
          <Text style={{ width: 210 }}>Fund Financial Statements</Text>
          <Tooltip title={blockedTip}>
            <span>
              <Button size='small' disabled={blocked} onClick={previewPack}>
                Preview
              </Button>
            </span>
          </Tooltip>
        </div>
        <div className='flex items-center gap-2'>
          <Text style={{ width: 210 }}>Capital Account Statement</Text>
          <Select
            size='small'
            style={{ minWidth: 260 }}
            value={selectedLp}
            showSearch
            optionFilterProp='label'
            placeholder='Select investor'
            aria-label='Investor'
            options={investorOptions}
            onChange={setSelectedLp}
          />
          {fundGroupId ? (
            <Select
              size='small'
              style={{ minWidth: 150 }}
              value={member}
              aria-label='Statement'
              options={memberOptions}
              onChange={setMember}
            />
          ) : null}
          <Tooltip title={blockedTip || (!selectedLp ? 'Select an investor first.' : '')}>
            <span>
              <Button size='small' disabled={blocked || !selectedLp} onClick={previewCapitalAccount}>
                Preview
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      <Text type='secondary' className='block mt-2'>
        A preview shows the statement as it would be generated now, from the books at the close of the period end;
        nothing is stored or sent.{' '}
        {/* a consolidated fund states no carried interest yet; a fund does once
            its waterfall runs, and the section below shows whether it does */}
        {fundGroupId
          ? 'The capital account at fair market value is stated before carried interest.'
          : 'The capital account at fair market value is stated after carried interest where Waterfall position, below, shows figures, and before carried interest otherwise.'}
      </Text>

      <Modal
        title={preview?.title ?? 'Statement preview'}
        open={preview !== null}
        onCancel={closePreview}
        onOk={() => void downloadPreview()}
        okText='Download PDF'
        cancelText='Close'
        okButtonProps={{ disabled: !preview?.blobUrl, loading: downloading }}
        width={920}
        destroyOnHidden
      >
        <div style={{ height: '72vh' }}>
          <ReportPreviewPane
            blobUrl={preview?.blobUrl ?? null}
            pageCount={preview?.pageCount ?? null}
            loading={preview?.loading ?? false}
            error={preview?.error ?? null}
            emptyText='Rendering the statement…'
          />
        </div>
      </Modal>
    </FormSection>
  )
}
